import React from 'react';
import { SideNavLink } from '@amzn/aac-chrome-internal-api';
import { Flex, Icon } from '@amzn/storm-ui';
import { renderFlag } from 'src/commons/components/LaunchFlag/LaunchFlag.utils';
import { externalLink } from '@amzn/storm-ui-icons';
import {
  LANDING_PLACEMENT,
  LandingAppIcon,
} from 'src/commons/components/LandingAppIcon/LandingAppIcon';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { CC_MODAL_MENU_ITEM_TOOLBAR_ID } from 'src/commons/constants';

interface SideNavLinkFeaturesProps {
  sideNavLink: SideNavLink;
  /**
   * Specifies whether the link is within the active category or is an top level nav link and is therefore focusable
   * @defaultValue `false`
   */
  isFocusable?: boolean;
  displayLandingApp?: boolean;
}

const SideNavLinkFeatures: React.VFC<SideNavLinkFeaturesProps> = ({
  sideNavLink,
  isFocusable = false,
  displayLandingApp = true,
}) => {
  const { landingAppName, updateLandingAppName } = useConsolidatedNavContext();
  const { link, sidebarItemType, isInternal, appName, calloutTag, id } =
    sideNavLink;
  const isExternalLink: boolean = sidebarItemType === 'EXTERNAL_LINK';
  // TODO: Remove hard-coded check for `CC_MODAL_MENU_ITEM_TOOLBAR_ID`. We need to create and use
  //  a landingApp specific conditional check by adding a setting in SidebarItemConfig
  // https://taskei.amazon.dev/tasks/09d7cc0e-7aad-4429-8c14-9724d5e5cf1c?activeTab=comments#4391cfcc-5121-4436-ba41-854e6be712bf
  const isLandingPageIconDisplayed: boolean =
    displayLandingApp &&
    id !== CC_MODAL_MENU_ITEM_TOOLBAR_ID &&
    !isExternalLink &&
    !isInternal &&
    (landingAppName === '' || landingAppName === appName);
  const linkHasFeatures: boolean =
    isLandingPageIconDisplayed || !!calloutTag || isExternalLink;

  if (!linkHasFeatures) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      data-ccx-e2e-id={`sidenav-link-features-${appName}`}
    >
      {!!calloutTag && renderFlag(sideNavLink)}
      {isExternalLink && <Icon type={externalLink} size="small" />}
      {isLandingPageIconDisplayed && !!updateLandingAppName && appName && (
        <LandingAppIcon
          appName={appName ?? ''}
          tooltipLabel={link.label}
          landingAppName={landingAppName ?? ''}
          landingPlacement={LANDING_PLACEMENT.SIDENAV}
          updateLandingAppName={updateLandingAppName}
          tabIndex={isFocusable ? 0 : -1}
        />
      )}
    </Flex>
  );
};

export default SideNavLinkFeatures;
